import React, { useMemo, useState } from 'react';
import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Building2 } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec_val'] as const;

interface PLEntry {
  line_type: string;
  category_id: string | null;
  product_code: string | null;
  description: string | null;
  period: string;
  entity_id: string | null;
  entity_name: string | null;
  amount_usd: number;
  amount_eur: number;
  amount: number;
  currency: string;
}

interface PLData {
  version: number;
  entries: PLEntry[];
}

interface PLEntityRow {
  id: string;
  entity_name: string;
  entity_group: string;
  metric_type: string;
  metric: string;
  jan: number; feb: number; mar: number;
  apr: number; may: number; jun: number;
  jul: number; aug: number; sep: number;
  oct: number; nov: number; dec_val: number;
  total: number;
}

const LINE_TYPE_ORDER: Record<string, number> = {
  revenue: 1,
  direct_opex: 2,
  indirect_opex: 3,
  ebitda: 4,
  capex: 5,
  net: 6,
};

const LINE_TYPE_LABELS: Record<string, string> = {
  revenue: 'Revenue',
  direct_opex: 'Direct Opex',
  indirect_opex: 'Indirect Opex',
  ebitda: 'EBITDA',
  capex: 'Capex',
  net: 'Net',
};

// formatCurrency is now a wrapper around useCurrency — call inside components only
function useFormatCurrency() {
  const { fmt } = useCurrency();
  return (amount: number, compact = false) => fmt(amount, 'EUR', { compact });
}

function getMonthIndex(period: string): number {
  const parts = period.split('-');
  return parts.length >= 2 ? parseInt(parts[1], 10) - 1 : -1;
}

export default function ProfitLoss() {
  const formatCurrency = useFormatCurrency();
  const { data, loading, error } = useFetch<PLData>('/api/fin/pl');
  const { data: entityData, loading: entityLoading } = useFetch<PLEntityRow[]>('/api/fin/pl-entities');
  const [displayCurrency, setDisplayCurrency] = useState<'usd' | 'eur'>('eur');
  const [activeTab, setActiveTab] = useState<'consolidated' | 'by-entity'>('consolidated');
  const [entityFilter, setEntityFilter] = useState('__all__');

  const getAmount = (entry: PLEntry) => {
    if (displayCurrency === 'usd' && entry.amount_usd) return entry.amount_usd;
    if (displayCurrency === 'eur' && entry.amount_eur) return entry.amount_eur;
    return entry.amount;
  };

  // Consolidated P&L data
  const { rows, monthlyTotals, chartData, summaryCards } = useMemo(() => {
    if (!data?.entries?.length) return { rows: [], monthlyTotals: {}, chartData: [], summaryCards: [] };

    type RowData = {
      key: string;
      lineType: string;
      category: string;
      description: string;
      months: Record<number, number>;
      total: number;
    };

    const rowMap = new Map<string, RowData>();

    for (const entry of data.entries) {
      const mi = getMonthIndex(entry.period);
      if (mi < 0) continue;
      const key = `${entry.line_type}|${entry.category_id || ''}|${entry.product_code || ''}|${entry.description || ''}`;

      if (!rowMap.has(key)) {
        rowMap.set(key, {
          key,
          lineType: entry.line_type,
          category: entry.category_id || '',
          description: entry.description || entry.product_code || entry.category_id || entry.line_type,
          months: {},
          total: 0,
        });
      }
      const row = rowMap.get(key)!;
      row.months[mi] = (row.months[mi] || 0) + getAmount(entry);
      row.total += getAmount(entry);
    }

    const allRows = Array.from(rowMap.values()).sort((a, b) => {
      const ao = LINE_TYPE_ORDER[a.lineType] || 99;
      const bo = LINE_TYPE_ORDER[b.lineType] || 99;
      if (ao !== bo) return ao - bo;
      return a.description.localeCompare(b.description);
    });

    const totals: Record<string, Record<number, number>> = {};
    for (const row of allRows) {
      if (!totals[row.lineType]) totals[row.lineType] = {};
      for (let m = 0; m < 12; m++) {
        totals[row.lineType][m] = (totals[row.lineType][m] || 0) + (row.months[m] || 0);
      }
    }

    const chart = MONTHS.map((label, idx) => ({
      month: label,
      Revenue: totals['revenue']?.[idx] || 0,
      Expenses: -((totals['direct_opex']?.[idx] || 0) + (totals['indirect_opex']?.[idx] || 0) + (totals['capex']?.[idx] || 0)),
    }));

    const totalRevenue = Object.values(totals['revenue'] || {}).reduce((s, v) => s + v, 0);
    const totalDirectOpex = Object.values(totals['direct_opex'] || {}).reduce((s, v) => s + v, 0);
    const totalIndirectOpex = Object.values(totals['indirect_opex'] || {}).reduce((s, v) => s + v, 0);
    const totalCapex = Object.values(totals['capex'] || {}).reduce((s, v) => s + v, 0);
    const ebitda = totalRevenue + totalDirectOpex + totalIndirectOpex;
    const netResult = ebitda + totalCapex;

    const cards = [
      { label: 'Revenue', value: totalRevenue, color: 'text-emerald-600' },
      { label: 'EBITDA', value: ebitda, color: ebitda >= 0 ? 'text-emerald-600' : 'text-red-500' },
      { label: 'Total Expenses', value: totalDirectOpex + totalIndirectOpex, color: 'text-red-500' },
      { label: 'Net Result', value: netResult, color: netResult >= 0 ? 'text-emerald-600' : 'text-red-500' },
    ];

    return { rows: allRows, monthlyTotals: totals, chartData: chart, summaryCards: cards };
  }, [data, displayCurrency]);

  // Entity P&L data
  const { entityNames, entityRows } = useMemo(() => {
    if (!entityData?.length) return { entityNames: [], entityRows: [] };

    const names = [...new Set(entityData.map(r => r.entity_name))].sort();
    let filtered = entityData;
    if (entityFilter !== '__all__') {
      filtered = entityData.filter(r => r.entity_name === entityFilter);
    }

    return { entityNames: names, entityRows: filtered };
  }, [entityData, entityFilter]);

  // Group entityRows by entity_name (must be before early returns per React hooks rules)
  const entitiesGrouped = useMemo(() => {
    const map = new Map<string, PLEntityRow[]>();
    for (const row of entityRows) {
      const list = map.get(row.entity_name) || [];
      list.push(row);
      map.set(row.entity_name, list);
    }
    return map;
  }, [entityRows]);

  if (loading && !data) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-4 md:p-6">
        <h1 className="text-2xl font-semibold mb-2">P&L Statement</h1>
        <Card><CardContent className="py-12 text-center text-muted-foreground">{error}</CardContent></Card>
      </div>
    );
  }

  const lineTypes = [...new Set(rows.map(r => r.lineType))];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground tracking-tight flex items-center gap-2">
            <TrendingUp size={24} /> P&L Statement
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Fin model v{data?.version || '?'} -- monthly breakdown
          </p>
        </div>
        <Select value={displayCurrency} onValueChange={v => setDisplayCurrency(v as 'usd' | 'eur')}>
          <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="usd">USD</SelectItem>
            <SelectItem value="eur">EUR</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {summaryCards.map(card => (
          <Card key={card.label} className="shadow-xs">
            <CardContent className="p-5">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">{card.label}</div>
              <div className={`text-2xl font-bold ${card.color}`}>
                {formatCurrency(card.value, true)}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="shadow-xs">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Monthly Revenue vs Expenses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  <Bar dataKey="Revenue" fill="#10B981" />
                  <Bar dataKey="Expenses" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab selector */}
      <div className="flex gap-2 border-b border-border">
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'consolidated'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('consolidated')}
        >
          <TrendingUp size={14} className="inline mr-1.5" />
          Consolidated P&L
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'by-entity'
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('by-entity')}
        >
          <Building2 size={14} className="inline mr-1.5" />
          P&L by Entity ({entityNames.length})
        </button>
      </div>

      {activeTab === 'consolidated' && (
        <Card className="shadow-xs">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider sticky left-0 bg-muted/30 min-w-[200px]">
                      Line Item
                    </th>
                    {MONTHS.map(m => (
                      <th key={m} className="text-right py-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[80px]">
                        {m}
                      </th>
                    ))}
                    <th className="text-right py-2 px-3 text-xs font-bold text-muted-foreground uppercase tracking-wider min-w-[100px]">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lineTypes.map(lt => {
                    const ltRows = rows.filter(r => r.lineType === lt);
                    const ltLabel = LINE_TYPE_LABELS[lt] || lt;
                    const isSummaryLine = lt === 'ebitda' || lt === 'net';

                    return (
                      <React.Fragment key={lt}>
                        <tr className={`border-b border-border ${isSummaryLine ? 'bg-muted/50 font-bold' : 'bg-muted/20'}`}>
                          <td className={`py-2 px-3 font-semibold text-foreground sticky left-0 ${isSummaryLine ? 'bg-muted/50' : 'bg-muted/20'}`}>
                            {ltLabel}
                            {!isSummaryLine && (
                              <Badge variant="secondary" className="ml-2 text-[10px]">{ltRows.length}</Badge>
                            )}
                          </td>
                          {MONTHS.map((_, mi) => {
                            const val = monthlyTotals[lt]?.[mi] || 0;
                            return (
                              <td key={mi} className={`py-2 px-2 text-right font-mono text-xs ${val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                                {val !== 0 ? formatCurrency(val) : '-'}
                              </td>
                            );
                          })}
                          <td className="py-2 px-3 text-right font-mono text-xs font-bold">
                            {(() => {
                              const total = Object.values(monthlyTotals[lt] || {}).reduce((s, v) => s + v, 0);
                              return (
                                <span className={total > 0 ? 'text-emerald-600' : total < 0 ? 'text-red-500' : ''}>
                                  {formatCurrency(total)}
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                        {!isSummaryLine && ltRows.map(row => (
                          <tr key={row.key} className="border-b border-border/50 hover:bg-muted/10">
                            <td className="py-1.5 px-3 pl-6 text-muted-foreground sticky left-0 bg-background">
                              {row.description}
                            </td>
                            {MONTHS.map((_, mi) => {
                              const val = row.months[mi] || 0;
                              return (
                                <td key={mi} className={`py-1.5 px-2 text-right font-mono text-xs ${val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                                  {val !== 0 ? formatCurrency(val) : '-'}
                                </td>
                              );
                            })}
                            <td className="py-1.5 px-3 text-right font-mono text-xs font-medium">
                              <span className={row.total > 0 ? 'text-emerald-600' : row.total < 0 ? 'text-red-500' : ''}>
                                {formatCurrency(row.total)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'by-entity' && (
        <div className="space-y-4">
          {/* Entity filter */}
          <Card>
            <CardContent className="p-4">
              <Select value={entityFilter} onValueChange={setEntityFilter}>
                <SelectTrigger className="w-64"><SelectValue placeholder="Select Entity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Entities</SelectItem>
                  {entityNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {entityLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : entityRows.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No entity P&L data available
              </CardContent>
            </Card>
          ) : (
            Array.from(entitiesGrouped.entries()).map(([entityName, eRows]) => (
              <Card key={entityName} className="shadow-xs">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Building2 size={14} /> {entityName}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/30">
                          <th className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider sticky left-0 bg-muted/30 min-w-[200px]">
                            Metric
                          </th>
                          {MONTHS.map(m => (
                            <th key={m} className="text-right py-2 px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[80px]">
                              {m}
                            </th>
                          ))}
                          <th className="text-right py-2 px-3 text-xs font-bold text-muted-foreground uppercase tracking-wider min-w-[100px]">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {eRows.map(row => {
                          const isSummary = row.metric_type === 'Net' || row.metric_type === 'Balances' || row.metric_type === 'Capital';
                          const total = MONTH_KEYS.reduce((s, k) => s + (row[k] || 0), 0);

                          return (
                            <tr
                              key={row.id}
                              className={`border-b border-border/50 ${isSummary ? 'bg-muted/30 font-semibold' : 'hover:bg-muted/10'}`}
                            >
                              <td className={`py-1.5 px-3 sticky left-0 ${isSummary ? 'bg-muted/30 font-semibold text-foreground' : 'bg-background text-muted-foreground pl-6'}`}>
                                {row.metric}
                                {!isSummary && (
                                  <Badge variant="secondary" className="ml-2 text-[10px]">{row.metric_type}</Badge>
                                )}
                              </td>
                              {MONTH_KEYS.map((k, mi) => {
                                const val = row[k] || 0;
                                return (
                                  <td key={mi} className={`py-1.5 px-2 text-right font-mono text-xs ${val > 0 ? 'text-emerald-600' : val < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                                    {val !== 0 ? formatCurrency(val) : '-'}
                                  </td>
                                );
                              })}
                              <td className="py-1.5 px-3 text-right font-mono text-xs font-medium">
                                <span className={total > 0 ? 'text-emerald-600' : total < 0 ? 'text-red-500' : ''}>
                                  {formatCurrency(total)}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
